import React, { useState, useEffect } from 'react'
import { MetricSection } from './MetricGrid'
import { MetricCardProps, MetricThresholds } from './MetricCard'
import { LoadingSpinner } from './LoadingSpinner'
import { PropertyResponse } from '@/services/api'
import { getRenovationCosts, calculateMonthlyPayment, safeNumber } from '@/utils/propertyCalculations'
import { Calculator, TrendingUp, Shield, DollarSign } from 'lucide-react'

interface ProfessionalMetricsProps {
  property: PropertyResponse
  loading?: boolean
}

interface AdvancedMetrics {
  irr: number
  npv: number
  mirr: number
  dscr: number
  breakEvenRatio: number
  grm: number
  oer: number
  debtYield: number
  equityMultiple: number
}

// Define industry benchmarks and thresholds for each metric
const METRIC_THRESHOLDS: Record<string, MetricThresholds> = {
  irr: { excellent: 15, good: 12, warning: 8 },
  npv: { excellent: 50000, good: 25000, warning: 0 },
  mirr: { excellent: 12, good: 10, warning: 6 },
  dscr: { excellent: 1.5, good: 1.25, warning: 1.1 },
  breakEvenRatio: { excellent: 80, good: 85, warning: 90 },
  grm: { excellent: 8, good: 10, warning: 12 },
  oer: { excellent: 30, good: 40, warning: 50 },
  debtYield: { excellent: 12, good: 10, warning: 8 },
  equityMultiple: { excellent: 2.5, good: 2.0, warning: 1.5 }
}

const METRIC_BENCHMARKS = {
  irr: "Industry avg: 10-12%",
  npv: "Positive NPV indicates profitable investment",
  mirr: "Industry avg: 8-10%",
  dscr: "Lenders typically require 1.25+",
  breakEvenRatio: "Lower is better, <85% is good",
  grm: "Lower is better, 8-12 is typical",
  oer: "Lower is better, <40% is good",
  debtYield: "Lenders typically require 10%+",
  equityMultiple: "2x+ indicates strong returns"
}

const METRIC_DESCRIPTIONS = {
  irr: "Internal Rate of Return - The annualized effective rate of return that makes NPV equal to zero. Measures the profitability of the investment.",
  npv: "Net Present Value - The difference between present value of cash inflows and outflows. Positive NPV indicates a profitable investment.",
  mirr: "Modified Internal Rate of Return - A more realistic measure that assumes reinvestment at a more conservative rate than IRR.",
  dscr: "Debt Service Coverage Ratio - Measures ability to service debt payments. NOI divided by annual debt service.",
  breakEvenRatio: "Break-Even Ratio - The occupancy rate required to cover all operating expenses and debt service.",
  grm: "Gross Rent Multiplier - Property price divided by gross annual rental income. Lower values indicate better deals.",
  oer: "Operating Expense Ratio - Operating expenses divided by gross operating income. Lower percentages indicate efficient operations.",
  debtYield: "Debt Yield - NOI divided by total loan amount. Measures return on the loan amount regardless of interest rate.",
  equityMultiple: "Equity Multiple - Total returns divided by initial equity investment. Measures total return over the holding period."
}

function calculateAdvancedMetrics(property: PropertyResponse): AdvancedMetrics {
  // Basic calculations from property data
  const purchasePrice = safeNumber(property.purchase_price)
  const annualRent = safeNumber(property.expected_monthly_rent) * 12
  const annualOperatingExpenses = safeNumber(property.annual_taxes) + safeNumber(property.annual_insurance) + (safeNumber(property.monthly_hoa_fees) * 12)
  const noi = annualRent - annualOperatingExpenses
  const loanAmount = safeNumber(property.loan_amount)
  const downPayment = safeNumber(property.down_payment)

  // Annual debt service calculation
  const monthlyPayment = calculateMonthlyPayment(loanAmount, safeNumber(property.interest_rate), safeNumber(property.loan_term, 30))
  const annualDebtService = monthlyPayment * 12

  // Calculate each metric (simplified calculations for demo)
  const irr = calculateIRR(property) // Placeholder - would need complex calculation
  const npv = calculateNPV(property) // Placeholder - would need discount rate and projections
  const mirr = irr * 0.85 // Simplified - MIRR is typically lower than IRR
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0
  const breakEvenRatio = annualRent > 0 ? ((annualOperatingExpenses + annualDebtService) / annualRent) * 100 : 0
  const grm = annualRent > 0 ? purchasePrice / annualRent : 0
  const oer = annualRent > 0 ? (annualOperatingExpenses / annualRent) * 100 : 0
  const debtYield = loanAmount > 0 ? (noi / loanAmount) * 100 : 0
  const equityMultiple = calculateEquityMultiple(property) // Placeholder

  return {
    irr,
    npv,
    mirr,
    dscr,
    breakEvenRatio,
    grm,
    oer,
    debtYield,
    equityMultiple
  }
}

// Placeholder functions for complex calculations
function calculateIRR(property: PropertyResponse): number {
  // This would involve complex NPV calculations with varying cash flows
  // For now, return a simplified calculation based on cash-on-cash return
  const annualRent = safeNumber(property.expected_monthly_rent) * 12
  const annualExpenses = safeNumber(property.annual_taxes) + safeNumber(property.annual_insurance)
  const monthlyPayment = calculateMonthlyPayment(
    safeNumber(property.loan_amount),
    safeNumber(property.interest_rate),
    safeNumber(property.loan_term, 30)
  )
  const annualCashFlow = annualRent - annualExpenses - (monthlyPayment * 12)
  const totalInvestment = safeNumber(property.down_payment) + getRenovationCosts(property.renovation_costs)

  return totalInvestment > 0 ? (annualCashFlow / totalInvestment) * 100 : 0
}

function calculateNPV(property: PropertyResponse): number {
  // Simplified NPV calculation - would need detailed projections
  const annualCashFlow = (safeNumber(property.expected_monthly_rent) * 12) - safeNumber(property.annual_taxes) - safeNumber(property.annual_insurance)
  const discountRate = 0.08 // 8% discount rate
  const holdingPeriod = 10 // 10 year holding period
  const totalInvestment = safeNumber(property.down_payment) + getRenovationCosts(property.renovation_costs)

  let npv = -totalInvestment
  for (let year = 1; year <= holdingPeriod; year++) {
    npv += annualCashFlow / Math.pow(1 + discountRate, year)
  }

  // Add terminal value (simplified)
  const terminalValue = safeNumber(property.purchase_price) * 1.5 // Assume 50% appreciation
  npv += terminalValue / Math.pow(1 + discountRate, holdingPeriod)

  return npv
}

function calculateEquityMultiple(property: PropertyResponse): number {
  // Simplified calculation - total returns over initial investment
  const initialInvestment = safeNumber(property.down_payment) + getRenovationCosts(property.renovation_costs)
  const annualCashFlow = (safeNumber(property.expected_monthly_rent) * 12) - safeNumber(property.annual_taxes) - safeNumber(property.annual_insurance)
  const holdingPeriod = 10
  const totalCashFlow = annualCashFlow * holdingPeriod
  const saleProceeds = safeNumber(property.purchase_price) * 1.5 - safeNumber(property.loan_amount) // Simplified

  return initialInvestment > 0 ? (totalCashFlow + saleProceeds) / initialInvestment : 0
}


export function ProfessionalMetrics({ property, loading = false }: ProfessionalMetricsProps) {
  const [metrics, setMetrics] = useState<AdvancedMetrics | null>(null)
  const [calculating, setCalculating] = useState(false)

  useEffect(() => {
    if (property && !loading) {
      setCalculating(true)
      // Simulate API call delay
      setTimeout(() => {
        const calculatedMetrics = calculateAdvancedMetrics(property)
        setMetrics(calculatedMetrics)
        setCalculating(false)
      }, 1000)
    }
  }, [property, loading])

  if (loading || calculating || !metrics) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Calculating advanced financial metrics...</p>
        </div>
      </div>
    )
  }

  // Organize metrics into logical sections
  const profitabilityMetrics: MetricCardProps[] = [
    {
      title: "Internal Rate of Return (IRR)",
      value: metrics.irr,
      format: "percentage",
      description: METRIC_DESCRIPTIONS.irr,
      benchmark: METRIC_BENCHMARKS.irr,
      thresholds: METRIC_THRESHOLDS.irr,
      trend: metrics.irr > 12 ? 'up' : metrics.irr > 8 ? 'neutral' : 'down'
    },
    {
      title: "Net Present Value (NPV)",
      value: metrics.npv,
      format: "currency",
      description: METRIC_DESCRIPTIONS.npv,
      benchmark: METRIC_BENCHMARKS.npv,
      thresholds: METRIC_THRESHOLDS.npv,
      trend: metrics.npv > 25000 ? 'up' : metrics.npv > 0 ? 'neutral' : 'down'
    },
    {
      title: "Modified IRR (MIRR)",
      value: metrics.mirr,
      format: "percentage",
      description: METRIC_DESCRIPTIONS.mirr,
      benchmark: METRIC_BENCHMARKS.mirr,
      thresholds: METRIC_THRESHOLDS.mirr,
      trend: metrics.mirr > 10 ? 'up' : metrics.mirr > 6 ? 'neutral' : 'down'
    }
  ]

  const riskMetrics: MetricCardProps[] = [
    {
      title: "Debt Service Coverage Ratio",
      value: metrics.dscr,
      format: "ratio",
      description: METRIC_DESCRIPTIONS.dscr,
      benchmark: METRIC_BENCHMARKS.dscr,
      thresholds: METRIC_THRESHOLDS.dscr,
      trend: metrics.dscr > 1.25 ? 'up' : metrics.dscr > 1.1 ? 'neutral' : 'down'
    },
    {
      title: "Break-Even Ratio",
      value: metrics.breakEvenRatio,
      format: "percentage",
      description: METRIC_DESCRIPTIONS.breakEvenRatio,
      benchmark: METRIC_BENCHMARKS.breakEvenRatio,
      thresholds: METRIC_THRESHOLDS.breakEvenRatio,
      trend: metrics.breakEvenRatio < 85 ? 'up' : metrics.breakEvenRatio < 90 ? 'neutral' : 'down'
    },
    {
      title: "Debt Yield",
      value: metrics.debtYield,
      format: "percentage",
      description: METRIC_DESCRIPTIONS.debtYield,
      benchmark: METRIC_BENCHMARKS.debtYield,
      thresholds: METRIC_THRESHOLDS.debtYield,
      trend: metrics.debtYield > 10 ? 'up' : metrics.debtYield > 8 ? 'neutral' : 'down'
    }
  ]

  const efficiencyMetrics: MetricCardProps[] = [
    {
      title: "Gross Rent Multiplier",
      value: metrics.grm,
      format: "ratio",
      description: METRIC_DESCRIPTIONS.grm,
      benchmark: METRIC_BENCHMARKS.grm,
      thresholds: METRIC_THRESHOLDS.grm,
      trend: metrics.grm < 10 ? 'up' : metrics.grm < 12 ? 'neutral' : 'down'
    },
    {
      title: "Operating Expense Ratio",
      value: metrics.oer,
      format: "percentage",
      description: METRIC_DESCRIPTIONS.oer,
      benchmark: METRIC_BENCHMARKS.oer,
      thresholds: METRIC_THRESHOLDS.oer,
      trend: metrics.oer < 40 ? 'up' : metrics.oer < 50 ? 'neutral' : 'down'
    },
    {
      title: "Equity Multiple",
      value: metrics.equityMultiple,
      format: "ratio",
      description: METRIC_DESCRIPTIONS.equityMultiple,
      benchmark: METRIC_BENCHMARKS.equityMultiple,
      thresholds: METRIC_THRESHOLDS.equityMultiple,
      trend: metrics.equityMultiple > 2 ? 'up' : metrics.equityMultiple > 1.5 ? 'neutral' : 'down'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 bg-primary-100 rounded-lg">
            <Calculator className="w-8 h-8 text-primary-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Professional Financial Metrics</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Advanced investment analysis using institutional-grade financial metrics.
          These calculations help professional investors make data-driven decisions.
        </p>
      </div>

      {/* Profitability Metrics */}
      <MetricSection
        title="Profitability Analysis"
        subtitle="Core return metrics that measure investment performance and profitability"
        metrics={profitabilityMetrics}
        columns={3}
        className="border-l-4 border-l-green-500"
      />

      {/* Risk Assessment */}
      <MetricSection
        title="Risk Assessment"
        subtitle="Financial stability and risk metrics for informed decision making"
        metrics={riskMetrics}
        columns={3}
        className="border-l-4 border-l-yellow-500"
      />

      {/* Efficiency Metrics */}
      <MetricSection
        title="Operational Efficiency"
        subtitle="Property performance and operational efficiency indicators"
        metrics={efficiencyMetrics}
        columns={3}
        className="border-l-4 border-l-blue-500"
      />

      {/* Footer Note */}
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> These calculations are based on current property data and standard industry assumptions.
          Actual performance may vary based on market conditions, property management, and other factors.
          Consult with a financial advisor for investment decisions.
        </p>
      </div>
    </div>
  )
}